import { Injectable } from '@angular/core';
import { AdMob, AdMobBannerSize, BannerAdPluginEvents, BannerAdPosition, BannerAdSize, InterstitialAdPluginEvents, RewardInterstitialAdPluginEvents } from '@capacitor-community/admob';
import { PluginListenerHandle } from '@capacitor/core';
import { Subscription, timer } from 'rxjs';

export enum AdMobCommand {
  showBanner = 'showBanner',
  removeBanner = 'removeBanner',
  showInterstitial = 'showInterstitial',
  removeInterstitial = 'removeInterstitial'
}

@Injectable({
  providedIn: 'root',
})
export class AdMobService {

  protected adUnitBanner: string = '';
  protected adUnitInterstitial: string = '';
  protected adUnitRewardInterstitial: string = '';

  protected initialized: boolean = false;
  protected adsEnabled: boolean = false;
  protected commandBuffer: AdMobCommand[] = [];

  protected interstitialStatus: 'idle' | 'loading' | 'ready' | 'showed' | 'closing' = 'idle';
  protected bannerStatus: 'idle' | 'loading' | 'ready' | 'showed' | 'closing' = 'idle';

  protected interstitialMinInterval: number = 2 * 60;
  protected interstitialLastTime: number = 0;

  protected bannerTimerSubscription: Subscription | undefined;
  protected commandTimerSubscription: Subscription | undefined;

  private adEventListeners: PluginListenerHandle[] = []; // Array to store all handles

  constructor() {
    this.setupGlobalAdListeners(); // Configure AdMob event listeners
  }

  public initialize() {
    try {
      //if (this.adsEnabled === true) {
        this.requestConsentForm().then( () => {
          AdMob.initialize({
            testingDevices: [], // Opcional: IDs de dispositivo para pruebas (ver docs)
            // disableSDKCrashReporting: false // Opcional
          }).then(() => {
            this.initialized = true;
            console.log('[HQ AppComponent] AdMob initialized successfully');
          });
        });
      //}
      //else {
      //  console.error('[HQ AppComponent] Error initializing Ads not enabled');
      //}
    } catch (e) {
      console.error('[HQ AppComponent] Error initialized AdMob', e);
    }
  }

  public destroy() {
    this.adEventListeners.forEach(handle => handle.remove()); // Remove all handles
    this.adEventListeners = []; // Clean array
    this.commandTimerSubscription?.unsubscribe();
    this.bannerTimerSubscription?.unsubscribe();
  }

  public setAdUnits(banner: string, interstitial: string, rewardInterstitial: string) {
    this.adUnitBanner = banner;
    this.adUnitInterstitial = interstitial;
    this.adUnitRewardInterstitial = rewardInterstitial;
  }

  public enableAds(enable: boolean) {
    this.adsEnabled = enable;
    // Initialize also when ads disabled, to perform user
    //if (this.adsEnabled === true) {
      this.initialize();
    //}
  }

  public executeCommand(cmd: AdMobCommand) {
    console.log('[AdMobService] command: ' + cmd);
    if (this.commandBuffer.length < 10) {
      this.commandBuffer.push(cmd);
      if (this.commandTimerSubscription === undefined) {
        this.commandTimerSubscription = timer(100).subscribe(() => this.handleCommandBuffer());
      }
    }
  }

  protected async handleCommandBuffer() {
    console.log('[AdMobService] handleCommandBuffer: START');
    while ((this.initialized === true)&&(this.commandBuffer.length > 0)) {
      try {
        const CMD: AdMobCommand = this.commandBuffer[0];
        console.log('[AdMobService] handleCommandBuffer: Command ' + CMD);
        // Show Interstitial
        if ((this.adsEnabled === true)&&(CMD === AdMobCommand.showInterstitial)) {

          // Check minimum time between interstitials
          const NOW: number = Math.round(Date.now() / 1000);  // Current time in seconds
          if (NOW > this.interstitialLastTime + this.interstitialMinInterval) {
            this.interstitialLastTime = NOW;
            await AdMob.prepareInterstitial({ adId: this.adUnitInterstitial }).then((res) => {
              console.log('[AdMobService] handleCommandBuffer: Interstitial prepared ' + JSON.stringify(res));
            });
            await AdMob.showInterstitial();
          }
        }
        // Show Banner
        else if ((this.adsEnabled === true)&&(CMD === AdMobCommand.showBanner)) {
          await AdMob.showBanner({ 
            adId: this.adUnitBanner,
            adSize: BannerAdSize.ADAPTIVE_BANNER, // Tamaño estándar del banner (320x50dp)
            position: BannerAdPosition.BOTTOM_CENTER,       // Posición en la pantalla
            margin: 58,
            // npa: true, // Descomentar para solicitar anuncios no personalizados (ej. para GDPR)
            // is).
          }).then((res) => {
            console.log('[AdMobService] handleCommandBuffer: Banner showed ' + JSON.stringify(res));
          });
        }
        // Remove Banner
        else if (CMD === AdMobCommand.removeBanner) {
          await AdMob.removeBanner().then((res) => {
            console.log('[AdMobService] handleCommandBuffer: Banner removed ' + JSON.stringify(res));
          });
        }
      }
      catch (e) {
        console.log('[AdMobService] handleCommandBuffer: ERROR ' + e);
      }
      console.log('[AdMobService] handleCommandBuffer: CMD Handled ' + this.commandBuffer[0]);
      this.commandBuffer.splice(0, 1);
    }
    this.commandTimerSubscription?.unsubscribe;
    this.commandTimerSubscription = undefined;

    if ((this.adsEnabled === true)&&(this.initialized === false)&&(this.commandBuffer.length > 0)) {
      this.commandTimerSubscription = timer(1000).subscribe(() => this.handleCommandBuffer());
    }
  }

  /*
  protected async showInterstitialAd() {
    if (this.adsEnabled === true) {
      try {
        // 1. Prepare and load Ad
        if (this.interstitialStatus === 'idle') {
          this.interstitialStatus = 'loading';
          console.log('[AdMobService] showInterstitialAd: Showing Interstitial...');
          await AdMob.prepareInterstitial({ adId: this.adUnitInterstitial });

          // 2. Show interstitial Ad (wait to be loaded)
          // Ad displayed from loaded event
        }
      } catch (e) {
        console.error('[AdMobService] showInterstitialAd: Error showing Interstitial Ad', e);
      }
    }
  }

  protected async showRewardInterstitialAd(adUnitId: string) {
    if (this.adsEnabled === true) {
      try {
        // 1. Prepare and load Ad
        await AdMob.prepareRewardInterstitialAd({ adId: this.adUnitRewardInterstitial });
        console.log('[AdMobService] showRewardInterstitialAd: Interstitial Ad prepared.');
    
        // 2. Show interstitial Ad (wait to be loaded)
        // Ad displayed from loaded event
        //await AdMob.showRewardInterstitialAd();
        //console.log('[AdMobService] showRewardInterstitialAd: Interstitial Ad displayed.');
    
      } catch (e) {
        console.error('[AdMobService] showRewardInterstitialAd: Error showing Interstitial Ad', e);
      }
    }
  }

  //
  // Muestra un anuncio tipo banner en la posición inferior central.
  //
  protected async showBannerAd(margin: number) {
    if (this.adsEnabled === true) {
      this.bannerTimerSubscription?.unsubscribe();
      if (this.bannerStatus === 'idle'){
        try {
          console.log('[AdMobService] showBannerAd: Showing banner...');
          await AdMob.showBanner({
            adId: this.adUnitBanner,
            adSize: BannerAdSize.ADAPTIVE_BANNER, // Tamaño estándar del banner (320x50dp)
            position: BannerAdPosition.BOTTOM_CENTER,       // Posición en la pantalla
            margin: margin,
            // npa: true, // Descomentar para solicitar anuncios no personalizados (ej. para GDPR)
            // is).
          });
          console.log('[AdMobService] showBannerAd: Banner showed');
        } catch (e) {
          console.error('Error al mostrar el anuncio Banner:', e);
        }
      }
      else {
        console.log('[AdMobService] showBannerAd: ERROR Status ' + this.bannerStatus);
      }
    }
  }

  //
  // Oculta el anuncio banner actualmente visible.
  //
  protected async hideBannerAd() {
    try {
      await AdMob.hideBanner();
      console.log('Anuncio Banner ocultado.');
    } catch (e) {
      console.error('Error al ocultar el anuncio Banner:', e);
    }
  }

  //
  // Elimina el anuncio banner de la vista. Se recomienda hacer esto al salir de una pantalla.
  // A diferencia de hideBanner, removeBanner libera recursos.
  //
  protected async removeBannerAd() {
    try {
      if (this.bannerStatus === 'showed') {
        console.log('[AdMobService] removeBannerAd: Removing banner...');
        await AdMob.removeBanner().then(() => this.bannerStatus = 'idle');
        console.log('[AdMobService] removeBannerAd: Removed banner');
      }
      else {
        console.log('[AdMobService] removeBannerAd: ERROR Status ' + this.bannerStatus);
      }
    } catch (e) {
      console.error('Error al eliminar el anuncio Banner:', e);
    }
  }
*/

  /**
   * Solicita el estado de consentimiento actual y, si es necesario,
   * muestra el formulario de consentimiento al usuario.
   */
  protected async requestConsentForm() {
    console.log('[AdMobService] requestConsentForm: Requesting consent...');
    try {
      // 1. Obtener el estado de consentimiento actual
      const consentInfo = await AdMob.requestConsentInfo();
      console.log('Consent Info:', consentInfo);

      // 2. Si el consentimiento es "Required" (requerido)
      if (consentInfo.status === 'REQUIRED') {
        // 3. Cargar y mostrar el formulario de consentimiento
        // Esto lanzará la ventana de consentimiento que configuraste en AdMob
        await AdMob.showConsentForm();
        console.log('Formulario de consentimiento mostrado.');

        // 4. Una vez que el usuario ha interactuado con el formulario,
        // puedes volver a verificar el estado para ver su elección.
        const newConsentInfo = await AdMob.requestConsentInfo();
        console.log('Nuevo estado de consentimiento tras interacción:', newConsentInfo);

        // 5. Cargar los anuncios después de que se haya obtenido el consentimiento
        // Opcional: solo si quieres cargar los anuncios justo después del consentimiento
        // this.loadAndShowAdsBasedOnConsent(newConsentInfo.status);

      } else {
        console.log('No se requiere mostrar el formulario de consentimiento o ya se obtuvo. Estado:', consentInfo.status);
        // Si el estado no es REQUIRED (ej. NOT_REQUIRED, OBTAINED),
        // puedes proceder a cargar los anuncios.
        // this.loadAndShowAdsBasedOnConsent(consentInfo.status);
      }

      // 6. Finalmente, inicializa el SDK de Anuncios y carga los anuncios
      // Esto DEBE hacerse después de obtener el consentimiento.
      // Puedes pasar una bandera para anuncios no personalizados si el consentimiento es limitado.
      //this.initializeAndLoadAds();

    } catch (error) {
      console.error('Error al solicitar o mostrar el formulario de consentimiento:', error);
      // Puedes intentar cargar anuncios no personalizados si hay un error con el consentimiento
      //this.initializeAndLoadAds(true); // Cargar no personalizados en caso de error
    }
  }

  private async setupGlobalAdListeners() {
    console.log('AdMobService: Configuring global listeners of AdMob...');

    // --- Listeners for Banner Ads ---
    this.adEventListeners.push(
      await AdMob.addListener(BannerAdPluginEvents.Loaded, () => {
        console.log('[AdMobService] EVENT: Banner loaded and ready to be displayed.');
        this.bannerStatus = 'ready';
        //this.bannerTimerSubscription = timer(15000).subscribe(() => {
        //  this.removeBannerAd();
        //});
      }),
      await AdMob.addListener(BannerAdPluginEvents.FailedToLoad, (error) => {
        this.bannerStatus = 'idle';
        console.error('[AdMobService] EVENT: Error loading banner: ', error.message);
      }),
      await AdMob.addListener(BannerAdPluginEvents.Opened, () => {
        console.log('[AdMobService] EVENT: Banner clicked and opened.');
        this.executeCommand(AdMobCommand.removeBanner);
        //this.removeBannerAd();
      }),
      await AdMob.addListener(BannerAdPluginEvents.Closed, () => {
        this.bannerStatus = 'idle';
        console.log('[AdMobService] EVENT: Banner closed.');
      }),
      await AdMob.addListener(BannerAdPluginEvents.SizeChanged, (size) => {
        console.log('[AdMobService] EVENT: Banner size changed. ' + JSON.stringify(size));
        if ((size.width === 0) && (size.height === 0)) {
          this.bannerStatus = 'idle';
        }
      }),
      await AdMob.addListener(BannerAdPluginEvents.AdImpression, () => {
        this.bannerStatus = 'showed';
        console.log('[AdMobService] EVENT: Banner impression.');
      })
    );

    // --- Listeners for Interstitial ---
    this.adEventListeners.push(
      await AdMob.addListener(InterstitialAdPluginEvents.Loaded, (result: any) => {
        console.log('[AdMobService] EVENT: Interstitial loaded and ready.');
        this.interstitialStatus = 'ready';
        //AdMob.showInterstitial();
      }),
      await AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (error) => {
        console.error('[AdMobService] EVENT: Error loading Interstitial:', error.message);
        this.interstitialStatus = 'idle';
      }),
      await AdMob.addListener(InterstitialAdPluginEvents.Showed, () => {
        console.log('[AdMobService] EVENT: Interstitial showed.');
        this.interstitialStatus = 'showed';
      }),
      await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
        console.log('[AdMobService] EVENT: Interstitial closed.');
        this.interstitialStatus = 'idle';
        // Aquí es un buen lugar para preparar el siguiente intersticial si se va a usar.
      }),
      await AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, (error) => {
        console.error('[AdMobService] EVENT: Error showing Interstitial:', error.message);
        this.interstitialStatus = 'idle';
      })
    );

    // --- Listeners for Reward Interstitial ---
    this.adEventListeners.push(
      await AdMob.addListener(RewardInterstitialAdPluginEvents.Loaded, () => {
        console.log('[AdMobService] EVENT: Reward Interstitial loaded and ready.');
      }),
      await AdMob.addListener(RewardInterstitialAdPluginEvents.FailedToLoad, (error) => {
        console.error('[AdMobService] EVENT: Error loading Reward Interstitial:', error.message);
      }),
      await AdMob.addListener(RewardInterstitialAdPluginEvents.Showed, () => {
        console.log('[AdMobService] EVENT: Reward Interstitial showed.');
      }),
      await AdMob.addListener(RewardInterstitialAdPluginEvents.Dismissed, () => {
        console.log('[AdMobService] EVENT: Reward Interstitial closed.');
      }),
      await AdMob.addListener(RewardInterstitialAdPluginEvents.FailedToShow, (error) => {
        console.error('[AdMobService] EVENT: Error showing Reward Interstitial:', error.message);
      }),
      await AdMob.addListener(RewardInterstitialAdPluginEvents.Rewarded, (rewardItem) => {
        console.log('[AdMobService] EVENT: Reward Interstitial closed. Item:', rewardItem);
        if (rewardItem && rewardItem.amount > 0) {
          console.log(`[AdMobService] EVENT User rewarded: ${rewardItem.amount} ${rewardItem.type}`);
          // AQUÍ: Lógica para dar la recompensa al usuario
        } else {
          console.log('[AdMobService] EVENT Reward Interstitial dismissed without reward.');
        }
      })
    );
  }
}
